import { useState, useMemo, useEffect, useCallback, useRef, type CSSProperties } from 'react';
import { Button } from '@helios/ui/button';
import { NavIcon, SidebarToggleButton, useBrandTokens } from '../../shell/shared';
import { useAppointmentsContext } from '../../shell/AppointmentsShellLayout';
import { usePowerSyncQuery, usePowerSyncDb } from '../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qFrontDeskSettings,
  qFrontDeskFieldSettings,
  qFrontDeskProviderSettings,
  qFrontDeskServiceSettings,
  qAppointmentProviders,
  qAppointmentServicesWithCategory,
  mapFrontDeskSettingsToUI,
  mapFrontDeskFieldToUI,
  qAppointmentStatuses,
  type FrontDeskSettingsRow,
  type FrontDeskFieldSettingRow,
  type FrontDeskProviderSettingRow,
  type FrontDeskServiceSettingRow,
  type AppointmentProviderRow,
  type AppointmentServiceWithCategoryRow,
  type AppointmentStatusRow,
} from '@helios/data-access';
import { FRONTDESK_BOOKING_DEFAULTS, DEFAULT_FRONTDESK_PATIENT_FIELDS } from '@helios/fixtures';

// ─── Types ───

interface LocalProvider {
  providerId: string;
  name: string;
  specialty: string;
  email: string;
  avatarColor: string;
  active: boolean;
  enabled: boolean;
}

interface LocalService {
  serviceId: string;
  name: string;
  categoryName: string;
  categoryColorHex: string;
  duration: number;
  priceCents: number | null;
  active: boolean;
  enabled: boolean;
  durationOverride: number | null;
}

interface LocalFrontDeskSettings {
  enabled: boolean;
  defaultAppointmentStatusId: string | null;
}

interface LocalField {
  id: string;
  fieldKey: string;
  label: string;
  section: string;
  sectionColor: { bg: string; color: string };
  enabled: boolean;
  required: boolean;
  locked: boolean;
}

// ─── Field label + section color mapping ───

const FIELD_LABELS: Record<string, string> = {
  patient_name: 'Patient Name',
  mobile_country_code: 'Mobile Country Code',
  mobile_number: 'Mobile Number',
  email: 'Email',
  date_of_birth: 'Date of Birth',
  gender: 'Gender',
  address_line_1: 'Address Line 1',
  city: 'City',
  state: 'Province/State',
  zip_code: 'Zip Code',
  id_type: 'ID Type',
  id_number: 'ID Number',
  nationality: 'Nationality',
  notes: 'Notes',
};

const SECTION_COLORS: Record<string, { bg: string; color: string }> = {
  'Personal Info': { bg: '#fce7f3', color: '#9d174d' },
  Contact: { bg: '#fef3c7', color: '#92400e' },
  Address: { bg: '#e0e7ff', color: '#3730a3' },
  Other: { bg: '#d1fae5', color: '#065f46' },
};

function fieldRowToLocal(row: FrontDeskFieldSettingRow): LocalField {
  const ui = mapFrontDeskFieldToUI(row);
  const section = ui.section || 'Other';
  return {
    id: ui.id,
    fieldKey: ui.fieldKey,
    label: FIELD_LABELS[ui.fieldKey] ?? ui.fieldKey,
    section,
    sectionColor: SECTION_COLORS[section] ?? { bg: '#f3f4f6', color: '#374151' },
    enabled: ui.enabled,
    required: ui.required,
    locked: ui.locked,
  };
}

// ─── Merge helpers ───

function mergeProviders(
  providerRows: AppointmentProviderRow[],
  settingRows: FrontDeskProviderSettingRow[],
): LocalProvider[] {
  return providerRows.map((r) => {
    const setting = settingRows.find((s) => s.provider_id === r.id);
    return {
      providerId: r.id,
      name: r.full_name ?? '',
      specialty: r.specialty ?? '',
      email: r.email ?? '',
      avatarColor: r.avatar_color ?? '#2980b9',
      active: r.active === 1,
      enabled: setting ? setting.enabled == 1 : true, // default enabled
    };
  });
}

function mergeServices(
  serviceRows: AppointmentServiceWithCategoryRow[],
  settingRows: FrontDeskServiceSettingRow[],
): LocalService[] {
  return serviceRows.map((r) => {
    const setting = settingRows.find((s) => s.service_id === r.id);
    return {
      serviceId: r.id,
      name: r.name ?? '',
      categoryName: r.category_name ?? '',
      categoryColorHex: r.category_color_hex ?? '#3b82f6',
      duration: r.typical_duration_mins ?? 0,
      priceCents: r.price_cents,
      active: r.active === 1,
      enabled: setting ? setting.enabled == 1 : true, // default enabled for front desk
      durationOverride: setting ? (setting.duration_override_mins ?? null) : null,
    };
  });
}

// ─── Toggle ───

function Toggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (val: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      style={{
        width: 44,
        height: 24,
        borderRadius: 'var(--radius-full)',
        background: checked ? 'var(--color-primary)' : 'var(--border)',
        border: 'none',
        padding: 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 150ms ease',
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: 'var(--radius-full)',
          background: '#ffffff',
          transform: checked ? 'translateX(20px)' : 'translateX(0)',
          transition: 'transform 150ms ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        }}
      />
    </button>
  );
}

// ─── Badge ───

function Badge({ label, bg, color }: { label: string; bg: string; color: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        padding: '0 var(--sp-2)',
        borderRadius: 'var(--radius-full)',
        background: bg,
        color,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

// ─── Required Badge ───

function RequiredBadge() {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        height: 24,
        padding: '0 var(--sp-2)',
        borderRadius: 'var(--radius-full)',
        background: '#fef3c7',
        color: '#92400e',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        whiteSpace: 'nowrap',
      }}
    >
      <NavIcon name="shield-alert" />
      Required
    </span>
  );
}

// ─── "From Service" badge ───

function FromServiceBadge() {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        padding: '0 var(--sp-2)',
        borderRadius: 'var(--radius-full)',
        background: '#dbeafe',
        color: '#1e40af',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        whiteSpace: 'nowrap',
      }}
    >
      From Service
    </span>
  );
}

// ─── Card ───

function Card({ children, style }: { children: React.ReactNode; style?: CSSProperties }) {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-6)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─── Hex to badge bg helper ───

function hexToBadgeBg(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, 0.15)`;
}

// ─── Front Desk Booking Settings Page ───

export function FrontDeskBookingSettingsPage() {
  useBrandTokens();
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const { collapsed, onToggleSidebar } = useAppointmentsContext();

  // ─── DB queries ───
  const { data: settingsRows = [] } = usePowerSyncQuery<FrontDeskSettingsRow>(qFrontDeskSettings());
  const { data: fieldRows = [] } =
    usePowerSyncQuery<FrontDeskFieldSettingRow>(qFrontDeskFieldSettings());
  const { data: statusRows = [] } = usePowerSyncQuery<AppointmentStatusRow>(qAppointmentStatuses());
  const { data: providerRows = [] } =
    usePowerSyncQuery<AppointmentProviderRow>(qAppointmentProviders());
  const { data: providerSettingRows = [] } = usePowerSyncQuery<FrontDeskProviderSettingRow>(
    qFrontDeskProviderSettings(),
  );
  const { data: serviceRows = [] } = usePowerSyncQuery<AppointmentServiceWithCategoryRow>(
    qAppointmentServicesWithCategory(),
  );
  const { data: serviceSettingRows = [] } = usePowerSyncQuery<FrontDeskServiceSettingRow>(
    qFrontDeskServiceSettings(),
  );

  // ─── Local buffered state ───
  const [localSettings, setLocalSettings] = useState<LocalFrontDeskSettings>({
    enabled: FRONTDESK_BOOKING_DEFAULTS.enabled,
    defaultAppointmentStatusId: FRONTDESK_BOOKING_DEFAULTS.default_appointment_status_id,
  });
  const [localFields, setLocalFields] = useState<LocalField[]>([]);
  const [localProviders, setLocalProviders] = useState<LocalProvider[]>([]);
  const [localServices, setLocalServices] = useState<LocalService[]>([]);
  const [fieldSearch, setFieldSearch] = useState('');
  const [providerSearch, setProviderSearch] = useState('');
  const [serviceSearch, setServiceSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  const initializedRef = useRef(false);
  const [savedSnapshot, setSavedSnapshot] = useState('');

  // ─── Populate from DB on first load ───
  // Only require providers + services (base tables) — field settings may not be seeded yet
  const allDataReady = providerRows.length > 0 && serviceRows.length > 0;

  const [initReady, setInitReady] = useState(false);
  useEffect(() => {
    if (!allDataReady || initReady) return;
    const t = setTimeout(() => setInitReady(true), 100);
    return () => clearTimeout(t);
  }, [allDataReady, initReady]);

  useEffect(() => {
    if (initializedRef.current) return;
    if (!initReady) return;

    // Settings row
    if (settingsRows.length > 0) {
      const ui = mapFrontDeskSettingsToUI(settingsRows[0]);
      setLocalSettings({
        enabled: ui.enabled,
        defaultAppointmentStatusId: ui.defaultAppointmentStatusId,
      });
    }

    // Field rows — if no rows synced yet, populate from fixtures
    const localFieldsInit =
      fieldRows.length > 0
        ? fieldRows.map(fieldRowToLocal)
        : DEFAULT_FRONTDESK_PATIENT_FIELDS.map((def) => ({
            id: def.field_key, // temporary ID until real rows arrive
            fieldKey: def.field_key,
            label: FIELD_LABELS[def.field_key] ?? def.field_key,
            section: def.section,
            sectionColor: SECTION_COLORS[def.section] ?? { bg: '#f3f4f6', color: '#374151' },
            enabled: def.enabled,
            required: def.required,
            locked: def.locked,
          }));
    setLocalFields(localFieldsInit);

    // Providers
    const mergedP = mergeProviders(providerRows, providerSettingRows);
    setLocalProviders(mergedP);

    // Services
    const mergedS = mergeServices(serviceRows, serviceSettingRows);
    setLocalServices(mergedS);

    // Compute snapshot inline
    const initSettings =
      settingsRows.length > 0
        ? (() => {
            const ui = mapFrontDeskSettingsToUI(settingsRows[0]);
            return {
              enabled: ui.enabled,
              defaultAppointmentStatusId: ui.defaultAppointmentStatusId,
            };
          })()
        : {
            enabled: FRONTDESK_BOOKING_DEFAULTS.enabled,
            defaultAppointmentStatusId: FRONTDESK_BOOKING_DEFAULTS.default_appointment_status_id,
          };
    const initFields = localFieldsInit;
    setSavedSnapshot(
      JSON.stringify({
        s: initSettings,
        f: initFields,
        p: mergedP.map((p) => ({ providerId: p.providerId, enabled: p.enabled })),
        sv: mergedS.map((s) => ({
          serviceId: s.serviceId,
          enabled: s.enabled,
          durationOverride: s.durationOverride,
        })),
      }),
    );

    initializedRef.current = true;
  }, [
    initReady,
    settingsRows,
    fieldRows,
    providerRows,
    providerSettingRows,
    serviceRows,
    serviceSettingRows,
  ]);

  const isDirty = useMemo(() => {
    if (!savedSnapshot) return false;
    const current = JSON.stringify({
      s: localSettings,
      f: localFields,
      p: localProviders.map((p) => ({ providerId: p.providerId, enabled: p.enabled })),
      sv: localServices.map((s) => ({
        serviceId: s.serviceId,
        enabled: s.enabled,
        durationOverride: s.durationOverride,
      })),
    });
    return current !== savedSnapshot;
  }, [localSettings, localFields, localProviders, localServices, savedSnapshot]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  }, []);

  // ─── Field handlers ───
  const filteredFields = useMemo(() => {
    if (!fieldSearch.trim()) return localFields;
    const q = fieldSearch.toLowerCase();
    return localFields.filter(
      (f) => f.label.toLowerCase().includes(q) || f.fieldKey.toLowerCase().includes(q),
    );
  }, [localFields, fieldSearch]);

  const handleToggleField = (id: string) => {
    setLocalFields((prev) =>
      prev.map((f) => (f.id === id && !f.locked ? { ...f, enabled: !f.enabled } : f)),
    );
  };

  // ─── Provider handlers ───
  const activeProviders = localProviders.filter((p) => p.active);
  const allProvidersEnabled = activeProviders.length > 0 && activeProviders.every((p) => p.enabled);

  const handleToggleAllProviders = () => {
    const newVal = !allProvidersEnabled;
    setLocalProviders((prev) => prev.map((p) => (p.active ? { ...p, enabled: newVal } : p)));
  };

  const filteredProviders = useMemo(() => {
    if (!providerSearch.trim()) return localProviders;
    const q = providerSearch.toLowerCase();
    return localProviders.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.specialty.toLowerCase().includes(q) ||
        p.email.toLowerCase().includes(q),
    );
  }, [localProviders, providerSearch]);

  // ─── Service handlers ───
  const activeServices = localServices.filter((s) => s.active);
  const allServicesEnabled = activeServices.length > 0 && activeServices.every((s) => s.enabled);

  const handleToggleAllServices = () => {
    const newVal = !allServicesEnabled;
    setLocalServices((prev) => prev.map((s) => (s.active ? { ...s, enabled: newVal } : s)));
  };

  const filteredServices = useMemo(() => {
    if (!serviceSearch.trim()) return localServices;
    const q = serviceSearch.toLowerCase();
    return localServices.filter(
      (s) => s.name.toLowerCase().includes(q) || s.categoryName.toLowerCase().includes(q),
    );
  }, [localServices, serviceSearch]);

  const handleToggleService = (serviceId: string) => {
    setLocalServices((prev) =>
      prev.map((s) => (s.serviceId === serviceId ? { ...s, enabled: !s.enabled } : s)),
    );
  };

  const handleServiceDurationOverride = (serviceId: string, value: string) => {
    const num = value === '' ? null : parseInt(value, 10);
    setLocalServices((prev) =>
      prev.map((s) =>
        s.serviceId === serviceId
          ? { ...s, durationOverride: num !== null && !isNaN(num) ? num : null }
          : s,
      ),
    );
  };

  // ─── Save ───
  const handleSave = useCallback(async () => {
    if (!db || saving) return;
    setSaving(true);

    try {
      const now = new Date().toISOString();

      // 1. Upsert settings row
      if (settingsRows.length > 0) {
        await db.execute(
          `UPDATE booking_frontdesk_settings
            SET enabled = ?, default_appointment_status_id = ?, updated_at = ?
            WHERE id = ?`,
          [
            localSettings.enabled ? 1 : 0,
            localSettings.defaultAppointmentStatusId,
            now,
            settingsRows[0].id,
          ],
        );
      } else {
        await db.execute(
          `INSERT INTO booking_frontdesk_settings
            (id, org_id, clinic_id, enabled, default_appointment_status_id, created_at, updated_at)
          VALUES (?, ?, NULL, ?, ?, ?, ?)`,
          [
            crypto.randomUUID(),
            orgId,
            localSettings.enabled ? 1 : 0,
            localSettings.defaultAppointmentStatusId,
            now,
            now,
          ],
        );
      }

      // 2. Upsert field settings — INSERT if no rows exist yet, UPDATE otherwise
      if (fieldRows.length === 0) {
        for (const field of localFields) {
          const newId = crypto.randomUUID();
          await db.execute(
            `INSERT INTO booking_frontdesk_patient_field_settings
              (id, org_id, clinic_id, field_key, enabled, required, locked, section, created_at, updated_at)
            VALUES (?, ?, NULL, ?, ?, ?, ?, ?, ?, ?)`,
            [
              newId,
              orgId,
              field.fieldKey,
              field.enabled ? 1 : 0,
              field.required ? 1 : 0,
              field.locked ? 1 : 0,
              field.section,
              now,
              now,
            ],
          );
        }
      } else {
        for (const field of localFields) {
          await db.execute(
            `UPDATE booking_frontdesk_patient_field_settings
              SET enabled = ?, required = ?, locked = ?, updated_at = ?
              WHERE id = ?`,
            [field.enabled ? 1 : 0, field.required ? 1 : 0, field.locked ? 1 : 0, now, field.id],
          );
        }
      }

      // 3. Upsert provider settings
      for (const p of localProviders) {
        await db.execute(
          `INSERT INTO booking_frontdesk_provider_settings
            (id, org_id, clinic_id, provider_id, enabled, created_at, updated_at)
          VALUES (?, ?, NULL, ?, ?, ?, ?)`,
          [crypto.randomUUID(), orgId, p.providerId, p.enabled ? 1 : 0, now, now],
        );
      }

      // 4. Upsert service settings
      for (const s of localServices) {
        await db.execute(
          `INSERT INTO booking_frontdesk_service_settings
            (id, org_id, clinic_id, service_id, enabled, duration_override_mins, created_at, updated_at)
          VALUES (?, ?, NULL, ?, ?, ?, ?, ?)`,
          [
            crypto.randomUUID(),
            orgId,
            s.serviceId,
            s.enabled ? 1 : 0,
            s.durationOverride,
            now,
            now,
          ],
        );
      }

      const snap = JSON.stringify({
        s: localSettings,
        f: localFields,
        p: localProviders.map((p) => ({ providerId: p.providerId, enabled: p.enabled })),
        sv: localServices.map((s) => ({
          serviceId: s.serviceId,
          enabled: s.enabled,
          durationOverride: s.durationOverride,
        })),
      });
      setSavedSnapshot(snap);
      showToast('Settings saved');
    } catch (err) {
      console.error('[FrontDeskBookingSettings] save error:', err);
      showToast('Failed to save settings');
    } finally {
      setSaving(false);
    }
  }, [
    db,
    saving,
    localSettings,
    localFields,
    localProviders,
    localServices,
    settingsRows,
    orgId,
    showToast,
  ]);

  // ─── Reset to Default ───
  const handleReset = useCallback(async () => {
    if (!db) return;

    const resetSettings: LocalFrontDeskSettings = {
      enabled: FRONTDESK_BOOKING_DEFAULTS.enabled,
      defaultAppointmentStatusId: FRONTDESK_BOOKING_DEFAULTS.default_appointment_status_id,
    };

    const resetFields: LocalField[] = localFields.map((f) => {
      const def = DEFAULT_FRONTDESK_PATIENT_FIELDS.find((d) => d.field_key === f.fieldKey);
      if (!def) return f;
      return {
        ...f,
        enabled: def.enabled,
        required: def.required,
        locked: def.locked,
      };
    });

    const resetProviders: LocalProvider[] = localProviders.map((p) => ({
      ...p,
      enabled: p.active, // all active providers enabled
    }));

    const resetServices: LocalService[] = localServices.map((s) => ({
      ...s,
      enabled: s.active, // all active services enabled for front desk
      durationOverride: null,
    }));

    setLocalSettings(resetSettings);
    setLocalFields(resetFields);
    setLocalProviders(resetProviders);
    setLocalServices(resetServices);

    try {
      const now = new Date().toISOString();

      // Settings
      if (settingsRows.length > 0) {
        await db.execute(
          `UPDATE booking_frontdesk_settings
            SET enabled = ?, default_appointment_status_id = ?, updated_at = ?
            WHERE id = ?`,
          [
            resetSettings.enabled ? 1 : 0,
            resetSettings.defaultAppointmentStatusId,
            now,
            settingsRows[0].id,
          ],
        );
      }

      // Fields — INSERT if no rows exist yet, UPDATE otherwise
      if (fieldRows.length === 0) {
        for (const field of resetFields) {
          await db.execute(
            `INSERT INTO booking_frontdesk_patient_field_settings
              (id, org_id, clinic_id, field_key, enabled, required, locked, section, created_at, updated_at)
            VALUES (?, ?, NULL, ?, ?, ?, ?, ?, ?, ?)`,
            [
              crypto.randomUUID(),
              orgId,
              field.fieldKey,
              field.enabled ? 1 : 0,
              field.required ? 1 : 0,
              field.locked ? 1 : 0,
              field.section,
              now,
              now,
            ],
          );
        }
      } else {
        for (const field of resetFields) {
          await db.execute(
            `UPDATE booking_frontdesk_patient_field_settings
              SET enabled = ?, required = ?, locked = ?, updated_at = ?
              WHERE id = ?`,
            [field.enabled ? 1 : 0, field.required ? 1 : 0, field.locked ? 1 : 0, now, field.id],
          );
        }
      }

      // Upsert provider settings
      for (const p of resetProviders) {
        await db.execute(
          `INSERT INTO booking_frontdesk_provider_settings
            (id, org_id, clinic_id, provider_id, enabled, created_at, updated_at)
          VALUES (?, ?, NULL, ?, ?, ?, ?)`,
          [crypto.randomUUID(), orgId, p.providerId, p.enabled ? 1 : 0, now, now],
        );
      }

      // Upsert service settings
      for (const s of resetServices) {
        await db.execute(
          `INSERT INTO booking_frontdesk_service_settings
            (id, org_id, clinic_id, service_id, enabled, duration_override_mins, created_at, updated_at)
          VALUES (?, ?, NULL, ?, ?, ?, ?, ?)`,
          [
            crypto.randomUUID(),
            orgId,
            s.serviceId,
            s.enabled ? 1 : 0,
            s.durationOverride,
            now,
            now,
          ],
        );
      }

      const snap = JSON.stringify({
        s: resetSettings,
        f: resetFields,
        p: resetProviders.map((p) => ({ providerId: p.providerId, enabled: p.enabled })),
        sv: resetServices.map((s) => ({
          serviceId: s.serviceId,
          enabled: s.enabled,
          durationOverride: s.durationOverride,
        })),
      });
      setSavedSnapshot(snap);
      showToast('Reset to defaults');
    } catch (err) {
      console.error('[FrontDeskBookingSettings] reset error:', err);
      showToast('Failed to reset settings');
    }
  }, [db, localFields, localProviders, localServices, settingsRows, orgId, showToast]);

  // ─── Status options from DB ───
  const statusOptions = useMemo(
    () =>
      statusRows.filter((s) => s.active === 1).map((s) => ({ id: s.id, label: s.label ?? s.id })),
    [statusRows],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Page Header */}
      <div
        style={{
          height: 'var(--clinic-header-height)',
          padding: '0 var(--sp-6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          {onToggleSidebar && (
            <SidebarToggleButton collapsed={collapsed} onToggle={onToggleSidebar} />
          )}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
              <h1
                style={{
                  fontSize: 'var(--font-size-xl2)',
                  fontWeight: 'var(--font-weight-semi)',
                  lineHeight: 'var(--line-height-xl2)',
                  letterSpacing: '-0.44px',
                  color: 'var(--text)',
                  margin: 0,
                }}
              >
                Front Desk Booking
              </h1>
              <span
                style={{
                  height: 22,
                  padding: '0 var(--sp-2)',
                  borderRadius: 'var(--radius-xs)',
                  background: 'var(--color-warning-bg)',
                  color: 'var(--color-warning-text)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-medium)',
                  lineHeight: '22px',
                  letterSpacing: '0.12px',
                }}
              >
                Admin only
              </span>
            </div>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
                margin: '2px 0 0',
              }}
            >
              Configure staff booking defaults and quick patient creation
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          <button
            type="button"
            onClick={handleReset}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              background: 'none',
              border: 'none',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <NavIcon name="refresh" />
            Reset to Default
          </button>
          <Button variant="gradient" size="md" disabled={!isDirty || saving} onClick={handleSave}>
            Save Changes
          </Button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#1f2937',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            zIndex: 9999,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {toast}
        </div>
      )}

      {/* Scrollable Content */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'var(--sp-6)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-5)',
        }}
      >
        {/* Card 1 — Quick Patient (Appointment Flow) */}
        <Card style={{ padding: 0 }}>
          <div style={{ padding: 'var(--sp-6) var(--sp-6) 0' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 'var(--font-size-lg)',
                    fontWeight: 'var(--font-weight-semi)',
                    color: 'var(--text)',
                  }}
                >
                  Quick Patient (Appointment Flow)
                </div>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text-muted)',
                    marginTop: 2,
                  }}
                >
                  Choose which patient fields staff capture when creating a new patient while
                  booking an appointment
                </div>
              </div>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <div
                  style={{
                    position: 'absolute',
                    left: 12,
                    color: 'var(--text-muted)',
                    display: 'flex',
                    pointerEvents: 'none',
                  }}
                >
                  <NavIcon name="search" />
                </div>
                <input
                  type="text"
                  placeholder="Search fields..."
                  value={fieldSearch}
                  onChange={(e) => setFieldSearch(e.target.value)}
                  style={searchInputStyle}
                />
              </div>
            </div>
          </div>

          {/* Fields table */}
          <div style={{ marginTop: 'var(--sp-4)' }}>
            {/* Header */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '70px 100px 1fr 100px',
                gap: 'var(--sp-3)',
                alignItems: 'center',
                padding: 'var(--sp-3) var(--sp-6)',
                borderBottom: '1px solid var(--border)',
                borderTop: '1px solid var(--border)',
              }}
            >
              <span style={thStyle}>ENABLED</span>
              <span style={thStyle}>REQUIRED</span>
              <span style={thStyle}>FIELD</span>
              <span style={thStyle}>SECTION</span>
            </div>

            {/* Rows */}
            {filteredFields.map((field) => (
              <div
                key={field.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '70px 100px 1fr 100px',
                  gap: 'var(--sp-3)',
                  alignItems: 'center',
                  padding: 'var(--sp-3) var(--sp-6)',
                  borderBottom: '1px solid var(--border)',
                  opacity: field.enabled ? 1 : 0.5,
                }}
              >
                <Toggle
                  checked={field.enabled}
                  onChange={() => handleToggleField(field.id)}
                  disabled={field.locked}
                />
                <div>{field.required && <RequiredBadge />}</div>
                <div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--text)',
                    }}
                  >
                    {field.label}
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    {field.fieldKey}
                  </div>
                </div>
                <Badge
                  label={field.section}
                  bg={field.sectionColor.bg}
                  color={field.sectionColor.color}
                />
              </div>
            ))}

            {filteredFields.length === 0 && (
              <div
                style={{
                  padding: 'var(--sp-6)',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                {fieldSearch ? 'No fields match your search.' : 'No fields configured yet.'}
              </div>
            )}

            {/* Note bar */}
            <div
              style={{
                margin: 'var(--sp-4) var(--sp-6) var(--sp-6)',
                padding: 'var(--sp-3) var(--sp-4)',
                borderRadius: 'var(--radius-md)',
                background: '#fef3c7',
                fontSize: 'var(--font-size-sm)',
                color: '#92400e',
              }}
            >
              <strong>Note:</strong> Full patient details can be completed later from Patient
              Profile.
            </div>
          </div>
        </Card>

        {/* Card 2 — Providers */}
        <Card>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                }}
              >
                Providers
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-muted)',
                  marginTop: 2,
                  marginBottom: 'var(--sp-4)',
                }}
              >
                Select which providers can be booked for front desk appointments
              </div>
            </div>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <div
                style={{
                  position: 'absolute',
                  left: 12,
                  color: 'var(--text-muted)',
                  display: 'flex',
                  pointerEvents: 'none',
                }}
              >
                <NavIcon name="search" />
              </div>
              <input
                type="text"
                placeholder="Search providers..."
                value={providerSearch}
                onChange={(e) => setProviderSearch(e.target.value)}
                style={searchInputStyle}
              />
            </div>
          </div>

          {/* Bulk toggle */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-3)',
              marginBottom: 'var(--sp-4)',
            }}
          >
            <Toggle checked={allProvidersEnabled} onChange={handleToggleAllProviders} />
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text)',
              }}
            >
              Enable all providers
            </span>
          </div>

          {/* Provider list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
            {filteredProviders.map((provider) => (
              <div
                key={provider.providerId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-3)',
                  opacity: provider.enabled ? 1 : 0.6,
                }}
              >
                <Toggle
                  checked={provider.enabled}
                  onChange={() =>
                    setLocalProviders((prev) =>
                      prev.map((p) =>
                        p.providerId === provider.providerId ? { ...p, enabled: !p.enabled } : p,
                      ),
                    )
                  }
                />
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 'var(--radius-full)',
                    background: provider.avatarColor,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-bold)',
                  }}
                >
                  {provider.name
                    .split(' ')
                    .map((w) => w[0])
                    .join('')
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--text)',
                    }}
                  >
                    {provider.name}
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                    {provider.specialty || provider.email || '\u2014'}
                  </div>
                </div>
                {!provider.active && <Badge label="Inactive" bg="#f3f4f6" color="#6b7280" />}
              </div>
            ))}
            {filteredProviders.length === 0 && (
              <div
                style={{
                  padding: 'var(--sp-4)',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                {providerSearch
                  ? 'No providers match your search.'
                  : 'No providers configured yet.'}
              </div>
            )}
          </div>
        </Card>

        {/* Card 3 — Bookable Services (Front Desk) */}
        <Card style={{ padding: 0 }}>
          <div style={{ padding: 'var(--sp-6) var(--sp-6) 0' }}>
            <div
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              Bookable Services (Front Desk)
            </div>
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
                marginTop: 2,
                marginBottom: 'var(--sp-4)',
              }}
            >
              Duration defaults to the service&apos;s typical duration unless overridden
            </div>

            {/* Bulk toggle + Search */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--sp-4)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
                <Toggle checked={allServicesEnabled} onChange={handleToggleAllServices} />
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--text)',
                  }}
                >
                  Enable all services
                </span>
              </div>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <div
                  style={{
                    position: 'absolute',
                    left: 12,
                    color: 'var(--text-muted)',
                    display: 'flex',
                    pointerEvents: 'none',
                  }}
                >
                  <NavIcon name="search" />
                </div>
                <input
                  type="text"
                  placeholder="Search services..."
                  value={serviceSearch}
                  onChange={(e) => setServiceSearch(e.target.value)}
                  style={searchInputStyle}
                />
              </div>
            </div>
          </div>

          {/* Services table */}
          <div>
            {/* Header */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '70px 1fr 130px 200px',
                gap: 'var(--sp-3)',
                alignItems: 'center',
                padding: 'var(--sp-3) var(--sp-6)',
                borderBottom: '1px solid var(--border)',
                borderTop: '1px solid var(--border)',
              }}
            >
              <span style={thStyle}>ENABLED</span>
              <span style={thStyle}>SERVICE</span>
              <span style={thStyle}>CATEGORY</span>
              <span style={thStyle}>DEFAULT DURATION</span>
            </div>

            {/* Rows */}
            {filteredServices.map((svc) => (
              <div
                key={svc.serviceId}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '70px 1fr 130px 200px',
                  gap: 'var(--sp-3)',
                  alignItems: 'center',
                  padding: 'var(--sp-3) var(--sp-6)',
                  borderBottom: '1px solid var(--border)',
                  opacity: svc.enabled ? 1 : 0.6,
                }}
              >
                <Toggle checked={svc.enabled} onChange={() => handleToggleService(svc.serviceId)} />
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--text)',
                  }}
                >
                  {svc.name}
                </span>
                <Badge
                  label={svc.categoryName || '\u2014'}
                  bg={hexToBadgeBg(svc.categoryColorHex)}
                  color={svc.categoryColorHex}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
                  {svc.durationOverride == null && <FromServiceBadge />}
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder={`${svc.duration}`}
                    value={svc.durationOverride ?? ''}
                    onChange={(e) =>
                      handleServiceDurationOverride(
                        svc.serviceId,
                        e.target.value.replace(/\D/g, ''),
                      )
                    }
                    style={{
                      width: 56,
                      height: 28,
                      padding: '0 var(--sp-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--font-size-sm)',
                      color: svc.durationOverride != null ? 'var(--text)' : 'var(--text-muted)',
                      background: 'var(--bg)',
                      textAlign: 'center',
                    }}
                  />
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                    min
                  </span>
                  {svc.durationOverride != null && (
                    <button
                      type="button"
                      title="Reset to service default"
                      onClick={() => handleServiceDurationOverride(svc.serviceId, '')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 24,
                        height: 24,
                        borderRadius: 'var(--radius-full)',
                        border: '1px solid var(--border)',
                        background: 'var(--bg-surface)',
                        cursor: 'pointer',
                        padding: 0,
                        color: 'var(--text-muted)',
                        fontSize: 12,
                      }}
                    >
                      <NavIcon name="x" />
                    </button>
                  )}
                </div>
              </div>
            ))}

            {filteredServices.length === 0 && (
              <div
                style={{
                  padding: 'var(--sp-6)',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                {serviceSearch ? 'No services match your search.' : 'No services configured yet.'}
              </div>
            )}
          </div>
        </Card>

        {/* Card 4 — Default Appointment Settings */}
        <Card>
          <div
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--text)',
              marginBottom: 'var(--sp-4)',
            }}
          >
            Default Appointment Settings
          </div>

          {/* Default status */}
          <div>
            <label
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text)',
                display: 'block',
                marginBottom: 'var(--sp-1)',
              }}
            >
              Default appointment status
            </label>
            <select
              value={localSettings.defaultAppointmentStatusId ?? ''}
              onChange={(e) =>
                setLocalSettings((prev) => ({
                  ...prev,
                  defaultAppointmentStatusId: e.target.value || null,
                }))
              }
              style={{
                height: 'var(--input-height)',
                padding: '0 var(--input-padding-x)',
                borderRadius: 'var(--input-radius)',
                border: '1px solid var(--border-input)',
                background: 'var(--input-bg)',
                fontSize: 'var(--font-size-base)',
                color: 'var(--input-text)',
                fontFamily: 'inherit',
                width: 260,
                cursor: 'pointer',
                appearance: 'auto',
              }}
            >
              <option value="">Select status...</option>
              {statusOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
                marginTop: 4,
              }}
            >
              Status automatically applied to new appointments
            </div>
          </div>

          {/* Allow creating new patient */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: 'var(--sp-5)',
              paddingTop: 'var(--sp-5)',
              borderTop: '1px solid var(--border)',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                }}
              >
                Allow creating new patient during booking
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-muted)',
                  marginTop: 2,
                }}
              >
                Staff can create a new patient while booking an appointment
              </div>
            </div>
            <Toggle
              checked={localSettings.enabled}
              onChange={(v) => setLocalSettings((prev) => ({ ...prev, enabled: v }))}
            />
          </div>
        </Card>

        {/* Card 5 — Queue (Coming Soon) */}
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            <span
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              Queue
            </span>
            <span
              style={{
                height: 22,
                padding: '0 var(--sp-2)',
                borderRadius: 'var(--radius-xs)',
                background: 'var(--bg-surface-2)',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-medium)',
                lineHeight: '22px',
                fontStyle: 'italic',
              }}
            >
              Coming soon
            </span>
          </div>
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              marginTop: 'var(--sp-2)',
            }}
          >
            Queue rules and check-in workflow will be configured here.
          </div>
        </Card>
      </div>
    </div>
  );
}

// ─── Shared styles ───

const thStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const searchInputStyle: CSSProperties = {
  width: 200,
  height: 36,
  padding: '0 var(--sp-4) 0 40px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  outline: 'none',
};
